import {Form, FormField, FormSubmitHandler, Label} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {usePostProformaGenerationMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

const proformaGenerationSchema = z.object({
  branchId: z.string().nonempty(),
  unify: z.boolean(),
});

type FormValues = z.infer<typeof proformaGenerationSchema>;

export function ProformaInvoiceGenerationNew() {
  const navigate = useNavigate();
  const [createProformaGeneration] = usePostProformaGenerationMutation();

  const {branchOptions, isLoading, isError} = useBranches();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await createProformaGeneration(data)
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.proformaGeneration);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {unify: false};

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('page.accounting.labels.newDefinition'),
      }}
      data-testid={testIds.settings.proformaGeneration('page')}
      isLoading={isLoading}
      isError={isError}
      isCreating
    >
      <Form<FormValues>
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        experimentalZodSchema={proformaGenerationSchema}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => (
          <VStack spacing={4}>
            <SettingsSection>
              <Grid columns={2}>
                <FormField
                  control={control}
                  type="choice"
                  name="branchId"
                  isNotClearable
                  options={branchOptions}
                  label={i18n.t('entity.cashRegister.parameters.branch')}
                  data-testid={testIds.settings.proformaGenerationNew('branch')}
                  isRequired
                />
              </Grid>
            </SettingsSection>
            <VStack spacing={1}>
              <Label>{i18n.t('page.accountingSettings.labels.generationType')}</Label>
              <FormField
                control={control}
                type="checkbox"
                data-testid={testIds.settings.proformaGenerationNew('unify')}
                name="unify"
                label={i18n.t('page.accountingSettings.labels.unify')}
              />
            </VStack>
            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  title: i18n.t('general.actions.discard'),
                  onClick: () => navigateWithoutBlocker(settingsRoutes.proformaGeneration),
                  variant: 'secondary',
                  'data-testid': testIds.settings.proformaGenerationNew('discard'),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  title: i18n.t('general.actions.create'),
                  'data-testid': testIds.settings.proformaGenerationNew('create'),
                },
              ]}
            />
          </VStack>
        )}
      </Form>
    </SettingsTemplate>
  );
}
